import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { api } from '@/services/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Plus, TrendingUp, Search, Calendar, IndianRupee, CheckCircle, Clock, Eye, Filter, PiggyBank, AlertCircle, Loader2, AlertTriangle, RefreshCw } from 'lucide-react';
import { formatCurrency, formatDate } from '@/utils/helpers';
import { formatFullName } from '@/utils/nameFormatter';

function LavajamManagement() {
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState([]);
  const [members, setMembers] = useState([]);
  const [mandalis, setMandalis] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Sync History States
  const [syncDialogOpen, setSyncDialogOpen] = useState(false);
  const [syncAccount, setSyncAccount] = useState(null);
  const [syncDate, setSyncDate] = useState('');
  const [syncPreview, setSyncPreview] = useState(null);
  const [syncing, setSyncing] = useState(false);

  const [filters, setFilters] = useState({
    status: 'all',
    mandali_id: 'all',
  });

  const [formData, setFormData] = useState({
    member_id: '',
    mandali_id: '',
    monthly_amount: '2000',
    start_date: new Date().toISOString().split('T')[0],
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      // Fetch members
      const membersData = await api.accountHolders.getAll();
      setMembers(membersData);

      // Fetch lavajam accounts
      const data = await api.lavajam.getAll();
      setAccounts(data.accounts || []);

      // Fetch mandalis (for super admin)
      try {
        const mandalisData = await api.mandalis.getAll();
        setMandalis(mandalisData);
      } catch (e) {
        // Manager doesn't have access
        console.log('Mandali fetch skipped');
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const accountData = {
        member_id: formData.member_id,
        mandali_id: formData.mandali_id,
        monthly_amount: parseFloat(formData.monthly_amount),
        start_date: formData.start_date,
      };

      // Check for backdated entry if not already confirmed
      if (!previewData) {
        const startDate = new Date(formData.start_date);
        const today = new Date();
        // Check if start date is in a previous month (or earlier year)
        const isPastDate = startDate.getFullYear() < today.getFullYear() ||
          (startDate.getFullYear() === today.getFullYear() && startDate.getMonth() < today.getMonth());

        if (isPastDate) {
          const preview = await api.lavajam.previewBackfill({
            start_date: formData.start_date,
            monthly_amount: parseFloat(formData.monthly_amount)
          });
          setPreviewData(preview);
          setSubmitting(false);
          return;
        }
      }

      await api.lavajam.create(accountData);

      toast.success('Lavajam account created successfully!');
      setDialogOpen(false);
      setPreviewData(null);
      fetchData();

      // Reset form
      setFormData({
        member_id: '',
        mandali_id: '',
        monthly_amount: '2000',
        start_date: new Date().toISOString().split('T')[0],
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create Lavajam account');
    } finally {
      setSubmitting(false);
    }
  };

  const openSyncDialog = (account) => {
    setSyncAccount(account);
    setSyncDate(account.start_date);
    setSyncPreview(null);
    setSyncDialogOpen(true);
  };

  const handlePreviewSync = async () => {
    if (!syncDate) return;
    setSyncing(true);
    try {
      const preview = await api.lavajam.previewBackfill({
        start_date: syncDate,
        monthly_amount: syncAccount.monthly_amount
      });
      setSyncPreview(preview);
    } catch (error) {
      toast.error('Failed to calculate preview');
    } finally {
      setSyncing(false);
    }
  };

  const handleConfirmSync = async () => {
    setSyncing(true);
    try {
      await api.lavajam.syncHistory(syncAccount.id, { start_date: syncDate });
      toast.success('Account history synced successfully');
      setSyncDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to sync history');
    } finally {
      setSyncing(false);
    }
  };

  const filteredAccounts = accounts.filter(account => {
    // Search filter
    const searchLower = searchQuery.toLowerCase();
    const memberName = formatFullName(account.member?.first_name, account.member?.middle_name, account.member?.last_name).toLowerCase();
    const accountNumber = account.account_number.toLowerCase();

    const matchesSearch = searchQuery === '' ||
      memberName.includes(searchLower) ||
      accountNumber.includes(searchLower) ||
      account.member?.contact_number?.includes(searchQuery);

    // Status filter
    const matchesStatus = filters.status === 'all' || account.status === filters.status;

    // Mandali filter
    const matchesMandali = filters.mandali_id === 'all' || account.mandali_id === filters.mandali_id;

    return matchesSearch && matchesStatus && matchesMandali;
  });

  const getStatusBadge = (status) => {
    const variants = {
      active: 'bg-green-100 text-green-800',
      closed: 'bg-gray-100 text-gray-800',
    };

    return (
      <Badge className={variants[status] || variants.active}>
        {status === 'active' ? '🟢 Active' : '🔴 Closed'}
      </Badge>
    );
  };

  const getPaymentStatus = (account) => {
    // This would need to check pending payments from the account details
    // For now, simplified version
    return (
      <Badge className="bg-blue-100 text-blue-800">
        ℹ️ View Details
      </Badge>
    );
  };

  if (loading) {
    return (
      <DashboardLayout role="manager">
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading Lavajam accounts...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout role="manager">
      <div className="space-y-6 fade-in pt-2">
        {/* Header - Consistent spacing */}
        <div className="flex justify-between items-center pb-2">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Lavajam Accounts</h1>
            <p className="text-sm text-gray-600 mt-1">Manage monthly savings (profit sharing) accounts</p>
          </div>

          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-emerald-600 hover:bg-emerald-700">
                <Plus className="mr-2 h-4 w-4" /> Create New Account
              </Button>
            </DialogTrigger>
            <DialogContent className="w-[95vw] max-w-2xl sm:w-full max-h-[90vh] overflow-y-auto p-4 md:p-6">
              <DialogHeader className="pb-4">
                <DialogTitle>{previewData ? 'Confirm Backdated Entry' : 'Create Lavajam Account'}</DialogTitle>
                <DialogDescription>
                  {previewData ? 'Please review the backdated payment calculation' : 'Start a new monthly savings account for a member'}
                </DialogDescription>
              </DialogHeader>

              {previewData ? (
                <div className="space-y-4 mt-4">
                  <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
                    <div className="flex items-start">
                      <AlertTriangle className="h-5 w-5 text-yellow-600 mt-0.5 mr-3" />
                      <div>
                        <h3 className="text-sm font-medium text-yellow-800">Backdated Account Detected</h3>
                        <p className="text-sm text-yellow-700 mt-1">
                          You are creating an account with a start date in the past. The system has calculated the pending dues.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="bg-gray-50 p-4 rounded-md">
                      <p className="text-sm text-gray-500">Start Date</p>
                      <p className="text-lg font-semibold">{formatDate(formData.start_date)}</p>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-md">
                      <p className="text-sm text-gray-500">Months Passed</p>
                      <p className="text-lg font-semibold">{previewData.months_passed}</p>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-md">
                      <p className="text-sm text-gray-500">Monthly Amount</p>
                      <p className="text-lg font-semibold">{formatCurrency(formData.monthly_amount)}</p>
                    </div>
                    <div className="bg-blue-50 p-4 rounded-md border border-blue-100">
                      <p className="text-sm text-blue-600">Total Payable Now</p>
                      <p className="text-xl font-bold text-blue-700">{formatCurrency(previewData.total_amount)}</p>
                    </div>
                  </div>

                  <div className="flex justify-end space-x-2 pt-4">
                    <Button variant="outline" onClick={() => setPreviewData(null)}>
                      Back to Edit
                    </Button>
                    <Button onClick={handleSubmit} disabled={submitting} className="bg-emerald-600 hover:bg-emerald-700">
                      {submitting ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Creating...
                        </>
                      ) : (
                        'Confirm & Create'
                      )}
                    </Button>
                  </div>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                  {/* Member Selection */}
                  <div className="space-y-2">
                    <Label htmlFor="member_id">Select Member *</Label>
                    <Select
                      value={formData.member_id}
                      onValueChange={(value) => setFormData({ ...formData, member_id: value })}
                      required
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Choose a member" />
                      </SelectTrigger>
                      <SelectContent>
                        {members.map(member => (
                          <SelectItem key={member.id} value={member.id}>
                            {formatFullName(member.first_name, member.middle_name, member.last_name)} ({member.account_number})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Mandali Selection (for super admin) */}
                  {mandalis.length > 0 && (
                    <div className="space-y-2">
                      <Label htmlFor="mandali_id">Mandali *</Label>
                      <Select
                        value={formData.mandali_id}
                        onValueChange={(value) => setFormData({ ...formData, mandali_id: value })}
                        required
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Choose mandali" />
                        </SelectTrigger>
                        <SelectContent>
                          {mandalis.map(mandali => (
                            <SelectItem key={mandali.id} value={mandali.id.toString()}>
                              {mandali.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  {/* Monthly Amount */}
                  <div className="space-y-2">
                    <Label htmlFor="monthly_amount">Monthly Amount (₹) *</Label>
                    <Input
                      id="monthly_amount"
                      type="number"
                      min="500"
                      max="10000"
                      step="100"
                      value={formData.monthly_amount}
                      onChange={(e) => setFormData({ ...formData, monthly_amount: e.target.value })}
                      required
                    />
                    <p className="text-sm text-gray-500">
                      Amount between ₹500 and ₹10,000
                    </p>
                  </div>

                  {/* Start Date */}
                  <div className="space-y-2">
                    <Label htmlFor="start_date">Start Date *</Label>
                    <Input
                      id="start_date"
                      type="date"
                      value={formData.start_date}
                      onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                      required
                    />
                  </div>

                  <div className="bg-blue-50 p-4 rounded-md text-sm text-blue-800">
                    <p className="font-semibold mb-1">ℹ️ Note:</p>
                    <ul className="list-disc list-inside space-y-1">
                      <li>Due date is fixed to the <strong>20th</strong> of every month.</li>
                      <li>This is a <strong>Profit Sharing</strong> account (no fixed interest).</li>
                    </ul>
                  </div>

                  <div className="flex justify-end space-x-2 pt-4">
                    <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} disabled={submitting}>
                      Cancel
                    </Button>
                    <Button type="submit" disabled={submitting} className="bg-emerald-600 hover:bg-emerald-700">
                      {submitting ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Creating...
                        </>
                      ) : (
                        'Create Account'
                      )}
                    </Button>
                  </div>
                </form>
              )}
            </DialogContent>
          </Dialog>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">Total Accounts</CardTitle>
              <PiggyBank className="h-4 w-4 text-gray-400" />
            </CardHeader>
            <CardContent>
              <div className="text-xl md:text-2xl font-bold">{accounts.length}</div>
              <p className="text-xs text-gray-500 mt-1">
                {accounts.filter(a => a.status === 'active').length} active
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">Total Deposited</CardTitle>
              <IndianRupee className="h-4 w-4 text-gray-400" />
            </CardHeader>
            <CardContent>
              <div className="text-lg md:text-2xl font-bold">
                {formatCurrency(accounts.reduce((sum, acc) => sum + parseFloat(acc.total_deposited || 0), 0))}
              </div>
              <p className="text-xs text-gray-500 mt-1">All active accounts</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">This Month Target</CardTitle>
              <TrendingUp className="h-4 w-4 text-gray-400" />
            </CardHeader>
            <CardContent>
              <div className="text-lg md:text-2xl font-bold">
                {formatCurrency(accounts.filter(a => a.status === 'active').reduce((sum, acc) => sum + parseFloat(acc.monthly_amount || 0), 0))}
              </div>
              <p className="text-xs text-gray-500 mt-1">Expected collection</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">Account Type</CardTitle>
              <CheckCircle className="h-4 w-4 text-gray-400" />
            </CardHeader>
            <CardContent>
              <div className="text-lg md:text-2xl font-bold">Profit Share</div>
              <p className="text-xs text-gray-500 mt-1">Variable returns</p>
            </CardContent>
          </Card>
        </div>

        {/* Filters & Search */}
        <Card>
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Search by name, account, phone..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>

              {/* Status Filter */}
              <Select value={filters.status} onValueChange={(value) => setFilters({ ...filters, status: value })}>
                <SelectTrigger>
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="closed">Closed</SelectItem>
                </SelectContent>
              </Select>

              {/* Mandali Filter (for super admin) */}
              {mandalis.length > 0 && (
                <Select value={filters.mandali_id} onValueChange={(value) => setFilters({ ...filters, mandali_id: value })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Filter by mandali" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Mandalis</SelectItem>
                    {mandalis.map(mandali => (
                      <SelectItem key={mandali.id} value={mandali.id.toString()}>
                        {mandali.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}

              {/* Clear Filters */}
              <Button
                variant="outline"
                onClick={() => {
                  setSearchQuery('');
                  setFilters({ status: 'all', mandali_id: 'all' });
                }}
              >
                Clear Filters
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Accounts Table - Desktop View */}
        <Card className="hidden md:block">
          <CardHeader>
            <CardTitle>Lavajam Accounts</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Account Number</TableHead>
                  <TableHead>Member</TableHead>
                  <TableHead>Monthly Amount</TableHead>
                  <TableHead>Total Deposited</TableHead>
                  <TableHead>Start Date</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Payment Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredAccounts.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8 text-gray-500">
                      {searchQuery || filters.status !== 'all' ?
                        'No accounts match your filters' :
                        'No Lavajam accounts yet. Create your first account!'
                      }
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredAccounts.map((account) => (
                    <TableRow key={account.id}>
                      <TableCell className="font-medium">{account.account_number}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center shadow-sm text-white font-bold text-xs">
                            {account.member?.first_name ? account.member.first_name.charAt(0) : ''}
                            {account.member?.last_name ? account.member.last_name.charAt(0) : ''}
                          </div>
                          <div>
                            <div className="font-medium">
                              {formatFullName(account.member?.first_name, account.member?.middle_name, account.member?.last_name)}
                            </div>
                            <div className="text-sm text-gray-500">{account.member?.contact_number}</div>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>{formatCurrency(account.monthly_amount)}</TableCell>
                      <TableCell className="font-semibold">{formatCurrency(account.total_deposited)}</TableCell>
                      <TableCell>{formatDate(account.start_date)}</TableCell>
                      <TableCell>{getStatusBadge(account.status)}</TableCell>
                      <TableCell>{getPaymentStatus(account)}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openSyncDialog(account)}
                            title="Sync History"
                          >
                            <RefreshCw className="h-4 w-4 text-gray-500" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate(`/manager/lavajam/${account.id}`)}
                          >
                            <Eye className="h-4 w-4 mr-1" /> View
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Accounts Cards - Mobile View */}
        <div className="md:hidden space-y-4">
          <div className="flex items-center justify-between px-1">
            <h3 className="text-lg font-semibold text-gray-800">Lavajam Accounts</h3>
            <span className="text-sm text-gray-500">{filteredAccounts.length} accounts</span>
          </div>

          {filteredAccounts.length === 0 ? (
            <Card className="p-8">
              <div className="text-center text-gray-500">
                {searchQuery || filters.status !== 'all' ?
                  'No accounts match your filters' :
                  'No Lavajam accounts yet. Create your first account!'
                }
              </div>
            </Card>
          ) : (
            filteredAccounts.map((account) => (
              <Card key={account.id} className="p-4 hover:shadow-md transition-shadow">
                {/* Member Info - Top */}
                <div className="flex items-center gap-3 mb-3 pb-3 border-b">
                  <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center shadow-sm text-white font-bold">
                    {account.member?.first_name ? account.member.first_name.charAt(0) : ''}
                    {account.member?.last_name ? account.member.last_name.charAt(0) : ''}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-base text-gray-900 truncate" title={formatFullName(account.member?.first_name, account.member?.middle_name, account.member?.last_name)}>
                      {formatFullName(account.member?.first_name, account.member?.middle_name, account.member?.last_name)}
                    </div>
                    <div className="text-sm text-gray-500">{account.member?.contact_number}</div>
                  </div>
                </div>

                {/* Account Number & Status */}
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <div className="text-xs text-gray-500">Account Number</div>
                    <div className="font-mono font-semibold text-sm">{account.account_number}</div>
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    {getStatusBadge(account.status)}
                    {getPaymentStatus(account)}
                  </div>
                </div>

                {/* Amounts */}
                <div className="grid grid-cols-2 gap-3 mb-3 p-3 bg-gray-50 rounded-lg">
                  <div>
                    <div className="text-xs text-gray-500 mb-1">Monthly Amount</div>
                    <div className="font-semibold text-emerald-600">{formatCurrency(account.monthly_amount)}</div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500 mb-1">Total Deposited</div>
                    <div className="font-bold text-green-600">{formatCurrency(account.total_deposited)}</div>
                  </div>
                </div>

                {/* Start Date */}
                <div className="mb-3">
                  <div className="text-xs text-gray-500">Start Date</div>
                  <div className="text-sm font-medium">{formatDate(account.start_date)}</div>
                </div>

                {/* Actions */}
                <div className="flex gap-2 pt-3 border-t">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => openSyncDialog(account)}
                    className="flex-1"
                  >
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Sync
                  </Button>
                  <Button
                    variant="default"
                    size="sm"
                    onClick={() => navigate(`/manager/lavajam/${account.id}`)}
                    className="flex-1 bg-gradient-to-r from-emerald-500 to-teal-500"
                  >
                    <Eye className="h-4 w-4 mr-2" />
                    View Details
                  </Button>
                </div>
              </Card>
            ))
          )}
        </div>
        {/* Sync History Dialog */}
        <Dialog open={syncDialogOpen} onOpenChange={setSyncDialogOpen}>
          <DialogContent className="w-[95vw] max-w-md sm:w-full max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Sync Account History</DialogTitle>
              <DialogDescription>
                Update the start date and generate missing payment records.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>Start Date</Label>
                <div className="flex gap-2">
                  <Input
                    type="date"
                    value={syncDate}
                    onChange={(e) => {
                      setSyncDate(e.target.value);
                      setSyncPreview(null);
                    }}
                  />
                  <Button onClick={handlePreviewSync} disabled={syncing || !syncDate}>
                    {syncing ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Preview'}
                  </Button>
                </div>
              </div>

              {syncPreview && (
                <div className="bg-gray-50 p-4 rounded-md space-y-3 border">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Months Passed:</span>
                    <span className="font-medium">{syncPreview.months_passed}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Total Amount:</span>
                    <span className="font-medium">{formatCurrency(syncPreview.total_amount)}</span>
                  </div>
                  {syncPreview.months_passed > 0 && (
                    <div className="bg-yellow-50 p-2 rounded text-xs text-yellow-800 flex items-start gap-2">
                      <AlertTriangle className="h-3 w-3 mt-0.5" />
                      <span>This will generate {syncPreview.months_passed} "Paid" records automatically.</span>
                    </div>
                  )}
                </div>
              )}

              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setSyncDialogOpen(false)}>Cancel</Button>
                <Button
                  onClick={handleConfirmSync}
                  disabled={syncing || !syncPreview}
                  className="bg-emerald-600 hover:bg-emerald-700"
                >
                  {syncing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  Confirm Sync
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}

export default LavajamManagement;
